import { formatDate } from "date-fns";

export type GroupMode = "all" | "trip" | "date";

interface GroupedBooking {
  groupKey: string;
  groupLabel: string;
  bookings: any[];
}

/**
 * Group bookings by trip name (tripId)
 */
export const groupByTripName = (bookings: any[]): GroupedBooking[] => {
  const groups: Record<string, any[]> = {};

  bookings.forEach((booking) => {
    const key = booking.tripId || "unknown";
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(booking);
  });

  return Object.entries(groups).map(([tripId, bookings]) => ({
    groupKey: tripId,
    groupLabel: bookings[0]?.tripTitle || tripId || "Unknown Trip",
    bookings,
  }));
};

/**
 * Group bookings by slot start date
 */
export const groupByStartDate = (bookings: any[]): GroupedBooking[] => {
  const groups: Record<string, any[]> = {};

  bookings.forEach((booking) => {
    const date = booking.slotStartDate
      ? formatDate(new Date(booking.slotStartDate), "dd MMM yyyy")
      : "No Date";
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(booking);
  });

  // Sort by date
  const sortedEntries = Object.entries(groups).sort((a, b) => {
    const dateA = new Date(a[0]).getTime();
    const dateB = new Date(b[0]).getTime();
    return dateB - dateA;
  });

  return sortedEntries.map(([date, bookings]) => ({
    groupKey: date,
    groupLabel: date,
    bookings,
  }));
};

/**
 * Get grouped bookings based on mode
 */
export const getGroupedBookings = (
  bookings: any[],
  mode: GroupMode
): GroupedBooking[] => {
  switch (mode) {
    case "trip":
      return groupByTripName(bookings);
    case "date":
      return groupByStartDate(bookings);
    case "all":
    default:
      return [
        {
          groupKey: "all",
          groupLabel: "All Bookings",
          bookings,
        },
      ];
  }
};
